import { World, ACT_IDLE, ACT_MOVE, ACT_BUILD, ACT_RECLAIM, ACT_REPAIR } from "./world";
import { DT, KIND_UNIT, MAX_ENTITIES } from "./constants";
import type { Sim } from "./sim";
import { dist, TAU } from "./math";

// Unit movement: order processing + flow-field following + local avoidance, then
// a hard, mass-weighted positional collision-resolution pass (GDD §9.3).
//
// Two layers, run in order:
//  1. STEERING (velocity): moving units follow the shared flow field toward their
//     goal plus a soft separation nudge so columns fan out and curve around
//     stationary units / solid obstacles. Units that have arrived, are building,
//     reclaiming, or holding at weapon range produce ZERO velocity — they hold
//     position, which is what makes destinations settle with no jitter/orbiting.
//  2. RESOLUTION (position): after integration, `resolveCollisions` iteratively
//     pushes overlapping bodies apart. It is radius/mass-aware (a tank displaces a
//     scout, not vice-versa), treats buildings/wreckage/features as immovable
//     (units are pushed fully out — no clipping), and gives stationary units a
//     mass boost so moving units flow around them instead of shoving them. Brief
//     overlap during resolution is fine; units settle non-overlapping.
//
// Determinism: everything iterates entities in ascending id order; the resolution
// pass accumulates displacements into scratch buffers and applies them after each
// sweep (order-independent within a sweep) and its degenerate (exactly-stacked)
// tie-break is a pure function of the entity id pair. No RNG, no wall clock.

const BUILD_PAD = 24;

// Group arrival: a plain-move unit settles when it reaches its point, or when it
// is walled off from a goal that friendlies have STABLY arrived at — so a blob
// packs into a stable disc instead of everyone grinding toward one pixel. Two
// gates keep this from freezing units that are only passing through: the blocker
// must be genuinely stable (not a body being shoved past the goal), and
// attack-move units are exempt entirely (they keep pushing until they arrive).
const GROUP_ARRIVE_CAP = 260; // max distance from goal at which a group settles
const ARRIVE_CONTACT = 6; // slop added to touching distance for the block test
const JAM_MOVE_EPS = 0.35; // world units/tick below which a unit counts as stable

// Soft separation (steering nudge, moving units only). Kept GENTLE: hard
// resolution already guarantees non-overlap, so this is only a smoothing hint. If
// it were strong enough to cancel the flow-field pull it would stall movers dead
// against a blob instead of letting them part it / peel around it.
const SEP_SOLID = 24; // push away from under-construction / not-yet-fielded solids
const SEP_STATIC = 14; // push away from stationary friendly units
const SEP_MOVING = 14; // push away from other moving units (fan columns out)
// Tangential avoidance: when a blocker sits ahead of a mover, add a sideways
// component so it peels around instead of grinding straight in. Side is chosen to
// keep making forward progress, with a deterministic id-parity tie-break so a
// symmetric wall doesn't leave the unit balanced dead-centre.
const TANGENT_GAIN = 1.2;

// Hard resolution. Overlaps are split by inverse "mass" (footprint area), so a
// tank displaces a scout and not vice-versa. An arrived ("settled") unit carries
// a mass BOOST so movers mostly flow around it and packing stays put — the boost
// is large for a plain mover (parked units barely yield; the mover squeezes past
// rather than shoving them) and small for an aggressive attack/attack-move mover
// (it PARTS the friendly blob in its way). Both are finite, so nothing hard-locks.
const RESOLVE_ITERS = 3;
const RESOLVE_MARGIN = 30; // query pad: covers largest obstacle radius + drift
const SETTLE_MULT_REGULAR = 8; // a settled unit's resistance to a plain mover
const SETTLE_MULT_ATTACK = 2.5; // ...and to an aggressive (attacking) mover

const scratchDir = { x: 0, y: 0 };
const dispX = new Float32Array(MAX_ENTITIES);
const dispY = new Float32Array(MAX_ENTITIES);
// Per-tick scratch, fully rewritten each tick (never read stale across ticks):
const moved = new Float32Array(MAX_ENTITIES); // distance travelled last tick
const aggressive = new Uint8Array(MAX_ENTITIES); // 1 = attacking / attack-moving

/** Collision "mass" derived from footprint area (no data change needed). */
function unitMass(radius: number): number {
  return radius * radius;
}

/**
 * Mass multiplier for unit `c` when resolving against `nb`. A moving unit has no
 * boost; a settled unit resists being shoved by whichever mover is pressing it —
 * strongly against a plain mover (so it barely budges and the mover squeezes past
 * / around), weakly against an attacker (so it gets parted aside).
 */
function settleMult(w: World, c: number, nb: number): number {
  if (w.action[c] === ACT_MOVE) return 1;
  return aggressive[nb] ? SETTLE_MULT_ATTACK : SETTLE_MULT_REGULAR;
}

export function movementStep(w: World, sim: Sim): void {
  // Pre-pass: how far each unit actually travelled last tick (jam / stability
  // signal), then snapshot this tick's start position for next time. Done up
  // front so every unit's value is available consistently to helpers below.
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.kind[e] !== KIND_UNIT) continue;
    moved[e] = Math.hypot(w.posX[e] - w.mvPrevX[e], w.posY[e] - w.mvPrevY[e]);
    w.mvPrevX[e] = w.posX[e];
    w.mvPrevY[e] = w.posY[e];
  }

  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.kind[e] !== KIND_UNIT || w.underConstruction(e)) continue;
    aggressive[e] = 0;
    const def = w.def(e);
    if (def.speed <= 0) continue;

    let dvx = 0;
    let dvy = 0;
    let act = ACT_IDLE;
    const order = w.orders[e][0];

    if (order) {
      if (order.kind === "build") {
        const bt = order.buildEnt;
        if (!w.isAlive(bt) || !w.underConstruction(bt)) {
          w.orders[e].shift();
        } else {
          const brange = w.def(bt).radius + def.radius + BUILD_PAD;
          const d = dist(w.posX[e], w.posY[e], w.posX[bt], w.posY[bt]);
          if (d <= brange) {
            act = ACT_BUILD;
            w.buildTargetEnt[e] = bt;
            sim.buildPowerOn[bt] += def.buildPower;
            faceToward(w, e, w.posX[bt], w.posY[bt], def.turnRate);
          } else {
            [dvx, dvy] = steer(w, sim, e, w.posX[bt], w.posY[bt], def.speed);
            act = ACT_MOVE;
          }
        }
      } else if (order.kind === "reclaim") {
        const tg = order.target;
        if (!w.isAlive(tg) || w.reclaimMetalStore[tg] + w.reclaimEnergyStore[tg] <= 0) {
          w.orders[e].shift();
        } else {
          const rrange = w.def(tg).radius + def.radius + BUILD_PAD;
          const d = dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]);
          if (d <= rrange) {
            act = ACT_RECLAIM;
            w.buildTargetEnt[e] = tg;
            sim.reclaimPowerOn[tg] += def.buildPower;
            faceToward(w, e, w.posX[tg], w.posY[tg], def.turnRate);
          } else {
            [dvx, dvy] = steer(w, sim, e, w.posX[tg], w.posY[tg], def.speed);
            act = ACT_MOVE;
          }
        }
      } else if (order.kind === "repair") {
        // One order that adapts to the target's live state as the builder arrives:
        //  - still under construction -> resume/assist it (drains metal+energy via
        //    the normal construction path in economy.ts). Requires this builder to
        //    be able to build that structure type, so orphaned sites are resumable
        //    only by a qualified faction builder.
        //  - completed but damaged     -> repair it (energy only, no metal).
        //  - completed and full health -> done.
        const tg = order.target;
        const resuming = w.isAlive(tg) && w.underConstruction(tg);
        const canBuildType = resuming && (def.buildOptions?.includes(w.def(tg).id) ?? false);
        if (!w.isAlive(tg) || (resuming && !canBuildType) || (!resuming && w.health[tg] >= w.maxHealth[tg])) {
          w.orders[e].shift();
        } else {
          const rrange = w.def(tg).radius + def.radius + BUILD_PAD;
          const d = dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]);
          if (d <= rrange) {
            w.buildTargetEnt[e] = tg;
            if (resuming) {
              act = ACT_BUILD;
              sim.buildPowerOn[tg] += def.buildPower;
            } else {
              act = ACT_REPAIR;
              sim.repairPowerOn[tg] += def.buildPower;
            }
            faceToward(w, e, w.posX[tg], w.posY[tg], def.turnRate);
          } else {
            [dvx, dvy] = steer(w, sim, e, w.posX[tg], w.posY[tg], def.speed);
            act = ACT_MOVE;
          }
        }
      } else if (order.kind === "attack") {
        const tg = order.target;
        if (!w.isAlive(tg)) {
          w.orders[e].shift();
        } else {
          w.targetEnt[e] = tg; // commanded target beats auto-acquire
          aggressive[e] = 1; // pressing an attack: part friendly blobs in the way
          const d = dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]);
          if (def.role === "bomb") {
            [dvx, dvy] = steer(w, sim, e, w.posX[tg], w.posY[tg], def.speed);
            act = ACT_MOVE;
          } else if (def.weapon && d <= def.weapon.range * 0.92) {
            faceToward(w, e, w.posX[tg], w.posY[tg], def.turnRate);
          } else {
            [dvx, dvy] = steer(w, sim, e, w.posX[tg], w.posY[tg], def.speed);
            act = ACT_MOVE;
          }
        }
      } else if (order.kind === "attackGround") {
        aggressive[e] = 1;
        const d = dist(w.posX[e], w.posY[e], order.x, order.y);
        if (def.weapon && d <= def.weapon.range * 0.92) {
          faceToward(w, e, order.x, order.y, def.turnRate);
        } else {
          [dvx, dvy] = steer(w, sim, e, order.x, order.y, def.speed);
          act = ACT_MOVE;
        }
      } else if (order.kind === "move") {
        const arriveR = def.radius * 2 + 12;
        const d = dist(w.posX[e], w.posY[e], order.x, order.y);
        if (order.attackMove) aggressive[e] = 1; // attack-move parts friendly blobs
        const engaged =
          order.attackMove &&
          w.isAlive(w.targetEnt[e]) &&
          def.weapon &&
          dist(w.posX[e], w.posY[e], w.posX[w.targetEnt[e]], w.posY[w.targetEnt[e]]) <=
            def.weapon.range * 0.92;
        if (engaged) {
          faceToward(w, e, w.posX[w.targetEnt[e]], w.posY[w.targetEnt[e]], def.turnRate);
        } else if (d <= arriveR) {
          w.orders[e].shift();
        } else if (
          !order.attackMove &&
          d <= GROUP_ARRIVE_CAP &&
          blockedNearGoal(w, sim, e, order.x, order.y, def.radius) &&
          goalOccupied(w, sim, e, order.x, order.y, arriveR)
        ) {
          // Walled off from a goal that friends have genuinely arrived at (a stable
          // cluster sits on it): settle into the packing. A unit passing/parting a
          // group has an open goal beyond it — no stable cluster there — so it never
          // trips this and flows on through.
          w.orders[e].shift();
        } else {
          [dvx, dvy] = steer(w, sim, e, order.x, order.y, def.speed);
          act = ACT_MOVE;
        }
      } else if (order.kind === "guard") {
        const tg = order.target;
        if (!w.isAlive(tg)) {
          w.orders[e].shift();
        } else {
          const d = dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]);
          if (d > 40) {
            [dvx, dvy] = steer(w, sim, e, w.posX[tg], w.posY[tg], def.speed);
            act = ACT_MOVE;
          }
        }
      }
    }

    if (act === ACT_MOVE) {
      // Soft separation + tangential avoidance so movers fan out and curve around
      // obstacles / parked units instead of grinding into them.
      const fmag = Math.hypot(dvx, dvy) || 1;
      const [sx, sy] = movingSeparation(w, sim, e, def.radius, dvx / fmag, dvy / fmag);
      let vx = dvx + sx;
      let vy = dvy + sy;
      const sp = Math.hypot(vx, vy);
      if (sp > def.speed) {
        const k = def.speed / sp;
        vx *= k;
        vy *= k;
      }
      w.velX[e] = vx;
      w.velY[e] = vy;
      if (sp > 1) faceToward(w, e, w.posX[e] + vx, w.posY[e] + vy, def.turnRate);
      w.posX[e] = w.gmap.clampX(w.posX[e] + vx * DT);
      w.posY[e] = w.gmap.clampY(w.posY[e] + vy * DT);
    } else {
      // Holding position (idle / building / reclaiming / at weapon range): no
      // steering, no separation force — zero velocity means zero jitter. The unit
      // only ever moves now via the hard resolution pass (if something intrudes).
      w.velX[e] = 0;
      w.velY[e] = 0;
    }

    // Combat runs later and may overwrite this to ACT_FIRE; safe to set here.
    w.action[e] = act;
  }

  // Hard, mass-weighted collision resolution against units and solid obstacles.
  resolveCollisions(w, sim);
}

function steer(w: World, sim: Sim, e: number, gx: number, gy: number, speed: number): [number, number] {
  const field = sim.flow.get(gx, gy, w.tick);
  field.sample(w.gmap, w.posX[e], w.posY[e], scratchDir);
  let dx = scratchDir.x;
  let dy = scratchDir.y;
  const d = dist(w.posX[e], w.posY[e], gx, gy);
  // Near the goal (or where the field is flat) steer straight at the target.
  if ((dx === 0 && dy === 0) || d < w.gmap.cellSize * 1.5) {
    dx = gx - w.posX[e];
    dy = gy - w.posY[e];
    const m = Math.hypot(dx, dy) || 1;
    dx /= m;
    dy /= m;
  }
  return [dx * speed, dy * speed];
}

/**
 * Is unit `e` blocked from its goal by a friendly that has already stopped? Used
 * to let a group settle into stable packing at a shared destination instead of
 * every unit orbiting the exact goal point.
 */
function blockedNearGoal(w: World, sim: Sim, e: number, gx: number, gy: number, rE: number): boolean {
  const px = w.posX[e];
  const py = w.posY[e];
  let gdx = gx - px;
  let gdy = gy - py;
  const gm = Math.hypot(gdx, gdy) || 1;
  gdx /= gm;
  gdy /= gm;
  let blocked = false;
  sim.hash.query(px, py, rE + 20, (o) => {
    if (blocked || o === e || !w.alive[o]) return;
    if (w.kind[o] !== KIND_UNIT || w.underConstruction(o)) return;
    if (w.owner[o] !== w.owner[e]) return;
    if (w.orders[o].length !== 0) return; // blocker must have arrived (idle)
    const dx = w.posX[o] - px;
    const dy = w.posY[o] - py;
    const d = Math.hypot(dx, dy);
    if (d <= 0.001 || d > rE + w.def(o).radius + ARRIVE_CONTACT) return;
    // ...and it must lie between us and the goal.
    if ((dx / d) * gdx + (dy / d) * gdy > 0.4) blocked = true;
  });
  return blocked;
}

/**
 * Has a friendly unit genuinely ARRIVED at this goal — i.e. is there a *stable*
 * (barely moving) idle friendly parked on it? A converging group has its goal
 * occupied this way; a unit merely passing or parting a group has an open goal
 * beyond it (any group members shoved near it are still moving, so they don't
 * count). This is the gate that keeps passers/parters flowing on through.
 */
function goalOccupied(w: World, sim: Sim, e: number, gx: number, gy: number, arriveR: number): boolean {
  const takenR = arriveR + w.def(e).radius + 8;
  const takenR2 = takenR * takenR;
  let taken = false;
  sim.hash.query(gx, gy, takenR, (o) => {
    if (taken || o === e || !w.alive[o]) return;
    if (w.kind[o] !== KIND_UNIT || w.underConstruction(o)) return;
    if (w.owner[o] !== w.owner[e] || w.orders[o].length !== 0) return; // idle friendly
    if (moved[o] >= JAM_MOVE_EPS) return; // must be stable, not just shoved past
    const dx = w.posX[o] - gx;
    const dy = w.posY[o] - gy;
    if (dx * dx + dy * dy <= takenR2) taken = true;
  });
  return taken;
}

/**
 * Soft separation steering for a MOVING unit: fan out, and flow *around* solid
 * obstacles and parked units rather than grinding into them. (fx,fy) is the
 * unit's forward (desired) direction; it is used to pick which way to peel around
 * a blocker so the unit keeps making progress toward its goal.
 */
function movingSeparation(
  w: World,
  sim: Sim,
  e: number,
  radius: number,
  fx: number,
  fy: number,
): [number, number] {
  let sx = 0;
  let sy = 0;
  const px = w.posX[e];
  const py = w.posY[e];
  const range = radius * 3 + 24;
  sim.hash.query(px, py, range, (o) => {
    if (o === e || !w.alive[o]) return;
    const odef = w.def(o);
    const dx = px - w.posX[o];
    const dy = py - w.posY[o];
    const d2 = dx * dx + dy * dy;
    const combined = radius + odef.radius + 4;
    if (d2 >= combined * combined || d2 <= 0.0001) return;
    const d = Math.sqrt(d2);
    const solid = w.kind[o] !== KIND_UNIT || w.underConstruction(o);
    const parked = !solid && w.action[o] !== ACT_MOVE;
    const strength = solid ? SEP_SOLID : parked ? SEP_STATIC : SEP_MOVING;
    const prox = (combined - d) / combined;
    // Radial push away from the neighbour.
    sx += (dx / d) * prox * strength;
    sy += (dy / d) * prox * strength;
    // Tangential steer-around for blockers that sit ahead of our travel.
    if (solid || parked) {
      const bnx = -dx / d; // unit -> blocker
      const bny = -dy / d;
      const ahead = fx * bnx + fy * bny;
      if (ahead > 0.1) {
        // Two perpendiculars; take the one that best preserves forward progress,
        // tie-broken deterministically by id parity so symmetric walls resolve.
        let tx = -bny;
        let ty = bnx;
        const dotA = tx * fx + ty * fy;
        const dotB = -tx * fx + -ty * fy;
        if (dotB > dotA || (dotB === dotA && (e & 1) === 1)) {
          tx = -tx;
          ty = -ty;
        }
        const tstr = ahead * prox * strength * TANGENT_GAIN;
        sx += tx * tstr;
        sy += ty * tstr;
      }
    }
  });
  return [sx, sy];
}

/**
 * Hard positional collision resolution. Iteratively pushes overlapping bodies
 * apart, mass-weighted (lighter/faster bodies yield to heavier and to stationary
 * ones); solid obstacles are immovable so units are pushed fully clear. Runs a
 * few relaxation sweeps per tick, accumulating into scratch buffers so a sweep is
 * order-independent and deterministic.
 */
function resolveCollisions(w: World, sim: Sim): void {
  const n = w.count;
  for (let iter = 0; iter < RESOLVE_ITERS; iter++) {
    for (let e = 0; e < n; e++) {
      dispX[e] = 0;
      dispY[e] = 0;
    }
    for (let e = 0; e < n; e++) {
      if (!w.alive[e] || w.kind[e] !== KIND_UNIT || w.underConstruction(e)) continue;
      const def = w.def(e);
      if (def.speed <= 0) continue;
      const rE = def.radius;
      const px = w.posX[e];
      const py = w.posY[e];
      let ax = 0;
      let ay = 0;
      sim.hash.query(px, py, rE + RESOLVE_MARGIN, (o) => {
        if (o === e || !w.alive[o]) return;
        const rO = w.def(o).radius;
        const min = rE + rO;
        const dx = px - w.posX[o];
        const dy = py - w.posY[o];
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min) return;
        let d: number;
        let nx: number;
        let ny: number;
        if (d2 < 1e-4) {
          // Exactly stacked: pick a deterministic per-pair direction so the pile
          // scatters into 2D (and e/o push exactly opposite).
          const lo = e < o ? e : o;
          const hi = e < o ? o : e;
          const hsh = (Math.imul(lo + 1, 0x9e3779b1) ^ Math.imul(hi + 1, 0x85ebca6b)) >>> 0;
          const ang = (hsh / 0x100000000) * TAU;
          nx = Math.cos(ang);
          ny = Math.sin(ang);
          if (e < o) {
            nx = -nx;
            ny = -ny;
          }
          d = 0;
        } else {
          d = Math.sqrt(d2);
          nx = dx / d;
          ny = dy / d;
        }
        const overlap = min - d;
        const solid = w.kind[o] !== KIND_UNIT || w.underConstruction(o);
        let share: number;
        if (solid) {
          share = 1; // obstacle is immovable: e takes the whole correction
        } else {
          // Split by inverse mass. A settled unit's mass is boosted by how hard the
          // *other* (moving) unit is pressing: gently for a plain mover (it flows
          // around), less so for an attacker (it parts through).
          const invMassE = 1 / (unitMass(rE) * settleMult(w, e, o));
          const invMassO = 1 / (unitMass(rO) * settleMult(w, o, e));
          share = invMassE / (invMassE + invMassO);
        }
        ax += nx * overlap * share;
        ay += ny * overlap * share;
      });
      dispX[e] = ax;
      dispY[e] = ay;
    }
    for (let e = 0; e < n; e++) {
      if (dispX[e] !== 0 || dispY[e] !== 0) {
        w.posX[e] = w.gmap.clampX(w.posX[e] + dispX[e]);
        w.posY[e] = w.gmap.clampY(w.posY[e] + dispY[e]);
      }
    }
  }
}

function faceToward(w: World, e: number, tx: number, ty: number, turnRate: number): void {
  const target = Math.atan2(ty - w.posY[e], tx - w.posX[e]);
  const maxStep = turnRate * DT;
  let d = target - w.heading[e];
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  if (Math.abs(d) <= maxStep) w.heading[e] = target;
  else w.heading[e] += Math.sign(d) * maxStep;
}
